"use client";
import { useCallback, useEffect, useRef } from "react";

export interface UseOAuthPopupArgs {
  authorize: (slug: string) => Promise<{ authorize_url: string }>;
  /** Origin we expect postMessage to come from. Same-origin in dev; in prod
   *  this is the plugin_platform_service public hostname. When omitted we
   *  accept any origin (dev convenience — production callers should set it). */
  expectedOrigin?: string;
  /** Timeout in ms before we give up waiting for the postMessage. */
  timeoutMs?: number;
}

export interface OAuthPopupResult {
  slug: string;
}

interface ActiveListener {
  handler: (e: MessageEvent) => void;
  timeoutId: ReturnType<typeof setTimeout>;
  popup: Window | null;
}

const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000;

/** Returns a stable callback that:
 *  1. Calls `authorize(slug)` to get the third-party authorize_url
 *  2. Opens it in a popup window
 *  3. Listens for a postMessage of shape
 *       { type: "connector_connected", slug: <slug> }
 *     from the (optional) expectedOrigin
 *  4. Resolves with { slug } or rejects on timeout / popup blocked.
 *
 *  Tracks the active listener + timer + popup in a ref so that an unmount
 *  mid-flight tears them down (no leaked listener, no setState-after-unmount
 *  when the 5-min timer eventually fires). */
/** The "{name}_oauth" form both sides of the OAuth dance settle on. */
function canonicalSlug(slug: string | undefined): string | undefined {
  if (!slug) return slug;
  return slug.endsWith("_oauth") ? slug : `${slug}_oauth`;
}

export function useOAuthPopup({
  authorize,
  expectedOrigin,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: UseOAuthPopupArgs) {
  const activeRef = useRef<ActiveListener | null>(null);

  const cleanup = useCallback(() => {
    if (activeRef.current) {
      window.removeEventListener("message", activeRef.current.handler);
      clearTimeout(activeRef.current.timeoutId);
      try {
        activeRef.current.popup?.close();
      } catch {
        /* popup may already be closed */
      }
      activeRef.current = null;
    }
  }, []);

  useEffect(() => cleanup, [cleanup]);

  return useCallback(
    async (slug: string): Promise<OAuthPopupResult> => {
      // Tear down any previous in-flight attempt before starting a new one.
      cleanup();
      const { authorize_url } = await authorize(slug);
      const popup = window.open(
        authorize_url,
        "connector-oauth",
        "width=500,height=600",
      );
      if (!popup) {
        throw new Error("popup_blocked");
      }
      return new Promise<OAuthPopupResult>((resolve, reject) => {
        const handler = (e: MessageEvent) => {
          if (expectedOrigin && e.origin !== expectedOrigin) return;
          const data = e.data as { type?: string; slug?: string } | null;
          // Compare canonically. The caller opens with the bare connector
          // name, but the server normalises to "{name}_oauth" before minting
          // the state, so that is the slug the callback reports back. A
          // strict equality check drops the message and leaves the caller
          // spinning on a connection that actually succeeded.
          //
          // Still an exact match once both sides are canonical: two popups
          // can be open at once, and resolving on another connector's
          // message would be worse than hanging.
          if (
            !data ||
            data.type !== "connector_connected" ||
            canonicalSlug(data.slug) !== canonicalSlug(slug)
          )
            return;
          cleanup();
          resolve({ slug });
        };
        const timeoutId = setTimeout(() => {
          cleanup();
          reject(new Error("oauth_timeout"));
        }, timeoutMs);
        activeRef.current = { handler, timeoutId, popup };
        window.addEventListener("message", handler);
      });
    },
    [authorize, expectedOrigin, timeoutMs, cleanup],
  );
}
